/**
 * English: Organizations admin — read-only list of Limbic active orgs (`is_active=true`).
 * Thai: หน้า Organizations — แสดงเฉพาะ org ที่ Limbic เปิดใช้ (is_active=true) อ่านอย่างเดียว
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/shared/Button';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import StatusBadge from '../components/shared/StatusBadge';
import { cn } from '../utils/cn';

type LimbicOrgRow = {
  org_name: string;
  display_name: string;
};

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<LimbicOrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const authHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [token]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(ENDPOINTS.adminOrganizations, {
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = (await res.json().catch(() => ({}))) as {
        organizations?: LimbicOrgRow[];
        error?: string;
      };
      if (!res.ok) {
        setError(data.error || res.statusText || t('organizations.loadError'));
        setRows([]);
        return;
      }
      setRows(data.organizations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('organizations.loadError'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.org_name.toLowerCase().includes(q) ||
        r.display_name.toLowerCase().includes(q),
    );
  }, [rows, filter]);

  return (
    <PageShell wide>
      <PageHeader
        title={t('organizations.title')}
        description={t('organizations.subtitlePick')}
      />

      {error ? (
        <div className="mb-4 rounded-xl border border-semantic-down/30 bg-semantic-down/10 px-4 py-3">
          <p className="text-sm text-semantic-down">{error}</p>
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
            {t('organizations.statActive')}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{rows.length}</p>
          <p className="mt-0.5 text-xs text-app-text-dim">{t('organizations.statActiveHint')}</p>
        </div>
        <div className="rounded-xl border border-app-border/80 bg-app-muted/15 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-app-text-dim">
            {t('organizations.statFromLimbic')}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{rows.length}</p>
          <p className="mt-0.5 text-xs text-app-text-dim">{t('organizations.summary', { total: rows.length })}</p>
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
        <div className="flex flex-col gap-4 border-b border-app-border/60 bg-app-muted/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="relative min-w-0 flex-1 sm:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-dim" />
            <input
              type="search"
              value={filter}
              onChange={(ev) => setFilter(ev.target.value)}
              placeholder={t('organizations.filterPlaceholder')}
              aria-label={t('organizations.filterLabel')}
              className="w-full rounded-lg border border-app-border bg-app-bg py-2.5 pl-9 pr-3 text-sm text-app-text placeholder:text-app-text-dim focus:outline-none focus:ring-2 focus:ring-app-accent/30"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void refresh()}
            disabled={loading}
            icon={<ArrowPathIcon className={cn('h-4 w-4', loading && 'animate-spin')} />}
            className="!rounded-lg shrink-0"
          >
            {t('organizations.refresh')}
          </Button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : rows.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-app-border bg-app-muted/10 px-6 py-16 text-center">
          <BuildingOffice2Icon className="mx-auto h-10 w-10 text-app-text-dim/50" />
          <p className="mt-4 text-sm text-app-text-sec">{t('organizations.empty')}</p>
        </section>
      ) : filtered.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-app-border bg-app-muted/10 px-6 py-16 text-center">
          <p className="text-sm text-app-text-sec">{t('organizations.filterEmpty')}</p>
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-2 border-b border-emerald-500/20 bg-emerald-500/[0.06] px-5 py-4 sm:px-6">
            <div>
              <h2 className="text-base font-semibold text-ink">{t('organizations.sectionActive')}</h2>
              <p className="mt-0.5 text-sm text-app-text-dim">{t('organizations.sectionActiveHint')}</p>
            </div>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
              {filtered.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-app-bg/60 text-left text-[11px] font-semibold uppercase tracking-wide text-app-text-dim">
                <tr>
                  <th className="px-5 py-3 sm:px-6 w-[14rem]">{t('organizations.colName')}</th>
                  <th className="px-5 py-3 sm:px-6">{t('organizations.colDisplay')}</th>
                  <th className="px-5 py-3 sm:px-6 w-[9rem]">{t('organizations.colStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border/50">
                {filtered.map((row) => (
                  <tr key={row.org_name} className="bg-emerald-500/[0.02] transition-colors hover:bg-app-muted/20">
                    <td className="px-5 py-3.5 sm:px-6 align-top">
                      <code className="inline-block max-w-[12rem] truncate rounded-md border border-app-border/80 bg-app-bg px-2 py-1 font-mono text-xs font-semibold text-app-accent">
                        {row.org_name}
                      </code>
                    </td>
                    <td className="px-5 py-3.5 sm:px-6 align-top">
                      <p className="font-medium text-ink leading-snug">{row.display_name || '—'}</p>
                    </td>
                    <td className="px-5 py-3.5 sm:px-6 align-top">
                      <StatusBadge variant="success" dot>
                        {t('organizations.active')}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </PageShell>
  );
}
